import React from 'react';
import {
  ArrowRightIcon,
  CameraIcon,
  CheckCircleIcon,
  MapPinIcon,
  SunIcon,
} from '@heroicons/react/24/solid';
import { Container } from '../common/Container';
import { BOOKING_DURATION_MINUTES, BOOKING_URL } from '../../config/contact';

const countyScreenshots = [
  {
    title: 'Denver property record',
    section: 'Key information',
    rows: [
      ['Schedule', '0125200092000'],
      ['Owner', 'SECURE CENTER LLC'],
      ['Class', 'MIXED-USE'],
    ],
  },
  {
    title: 'Denver actual values',
    section: '2026 actual values',
    rows: [
      ['Land', '$3,761,500'],
      ['Improvements', '$7,236,600'],
      ['Total', '$10,998,100'],
    ],
  },
];

export const Hero: React.FC = () => {
  const scrollToHowItWorks = () => {
    document.getElementById('how-it-works')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <section className="morning-hero relative overflow-hidden bg-brand-blue text-white">
      <div className="morning-glow pointer-events-none absolute -left-32 -top-40 h-[34rem] w-[34rem] rounded-full" />
      <div className="pointer-events-none absolute inset-x-0 bottom-0 h-44 bg-gradient-to-t from-brand-blue/45 to-transparent" />

      <Container className="relative py-16 lg:py-24">
        <div className="grid items-center gap-14 lg:grid-cols-[0.82fr_1.18fr]">
          <div className="morning-briefing">
            <div className="inline-flex items-center gap-2 rounded-full border border-amber-100/25 bg-white/10 px-3 py-1.5 text-sm font-semibold text-amber-50 backdrop-blur-sm">
              <SunIcon className="h-4 w-4 text-amber-200" />
              Good morning
            </div>

            <h1 className="mt-6 max-w-2xl text-5xl font-bold leading-[1.05] tracking-tight text-white sm:text-6xl">
              8 counties published new values overnight.
            </h1>
            <p className="mt-6 max-w-xl text-xl leading-relaxed text-brand-blue-100">
              235 properties are already updated with values, property details, and county-source screenshots.
            </p>

            <div className="morning-status mt-7 inline-flex items-center gap-3 rounded-xl border border-white/15 bg-white/10 px-4 py-3 text-sm text-white/90 backdrop-blur-sm">
              <CheckCircleIcon className="h-5 w-5 text-emerald-300" />
              Updates completed by 6:12 AM
            </div>

            <div className="mt-9 flex flex-col gap-4 sm:flex-row">
              <a
                href={BOOKING_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-brand-red px-7 py-4 font-semibold text-white transition-colors hover:bg-brand-red-600"
              >
                Book a {BOOKING_DURATION_MINUTES}-minute workflow review
                <ArrowRightIcon className="h-5 w-5" />
              </a>
              <button
                type="button"
                onClick={scrollToHowItWorks}
                className="inline-flex items-center justify-center rounded-lg border border-white/60 px-7 py-4 font-semibold text-white transition-colors hover:bg-white/10"
              >
                See how it works
              </button>
            </div>
          </div>

          <div className="morning-property-view overflow-hidden rounded-2xl border border-white/20 bg-brand-white-200 text-brand-blue shadow-[0_30px_80px_rgba(0,16,27,0.38)]">
            <div className="flex items-center gap-3 bg-brand-blue px-4 py-3 text-white">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-xs font-black text-brand-blue">AA</div>
              <div>
                <p className="text-xs font-semibold text-white">Appeal Advantage</p>
                <p className="text-[10px] text-brand-blue-100">Property view</p>
              </div>
              <span className="ml-auto rounded-md bg-white/10 px-2.5 py-1 text-xs text-white">Tax year 2026</span>
            </div>

            <div className="border-b border-gray-200 bg-white px-5 py-4">
              <p className="text-xs font-semibold text-brand-white-600">NSR – Northstar Realty</p>
              <div className="mt-1.5 flex items-start justify-between gap-4">
                <div>
                  <h2 className="text-xl font-bold text-brand-blue">12445 E 39th Ave</h2>
                  <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-1 text-xs text-brand-white-700">
                    <span>Parcel 01252-00-092-000</span>
                    <span className="inline-flex items-center gap-1">
                      <MapPinIcon className="h-3.5 w-3.5" />
                      Denver, CO
                    </span>
                  </div>
                </div>
                <span className="whitespace-nowrap rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700 ring-1 ring-emerald-200">
                  Values updated
                </span>
              </div>
            </div>

            <div className="flex gap-5 border-b border-gray-200 bg-white px-5 text-xs font-semibold text-brand-white-600">
              {['Value', 'Deadlines', 'Tracking', 'Property', 'Documents'].map((tab, index) => (
                <span key={tab} className={`py-3 ${index === 3 ? 'border-b-2 border-brand-red text-brand-blue' : ''}`}>
                  {tab}
                </span>
              ))}
            </div>

            <div className="p-5">
              <div className="grid gap-3 sm:grid-cols-2">
                <div className="rounded-xl border border-brand-blue-100 bg-white p-4">
                  <p className="text-xs font-semibold text-brand-white-600">2026 county actual value</p>
                  <p className="mt-1 text-2xl font-bold text-brand-blue">$10,998,100</p>
                  <p className="mt-1 text-xs text-emerald-700">Updated today at 6:12 AM</p>
                </div>
                <div className="rounded-xl border border-brand-blue-100 bg-white p-4">
                  <p className="text-xs font-semibold text-brand-white-600">Property record</p>
                  <p className="mt-1 text-lg font-bold text-brand-blue">Current and verified</p>
                  <p className="mt-1 text-xs text-brand-white-600">County source archived</p>
                </div>
              </div>

              <div className="mt-5 flex items-center gap-2">
                <CameraIcon className="h-4 w-4 text-brand-blue-400" />
                <h3 className="text-sm font-bold text-brand-blue">County Page Screenshots</h3>
                <span className="text-xs text-brand-white-600">2 archived</span>
              </div>

              <div className="mt-3 grid grid-cols-2 gap-3">
                {countyScreenshots.map((screenshot) => (
                  <article key={screenshot.title} className="overflow-hidden rounded-lg border border-brand-blue-100 bg-white shadow-sm">
                    <div className="h-40 overflow-hidden bg-[#f3f5f7] p-2.5">
                      <div className="h-full overflow-hidden rounded-sm border border-gray-300 bg-white shadow-sm">
                        <div className="bg-[#263f55] px-2 py-1 text-[7px] font-semibold uppercase tracking-wide text-white">
                          City and County of Denver
                        </div>
                        <div className="border-b border-gray-200 bg-[#f7f8f9] px-2 py-1.5">
                          <p className="text-[8px] font-bold text-[#263f55]">Property Record Search</p>
                          <p className="mt-0.5 text-[6px] text-gray-500">12445 E 39TH AVE · DENVER CO 80239</p>
                        </div>
                        <div className="p-2">
                          <p className="border-b border-[#2d6b89] pb-1 text-[7px] font-bold uppercase tracking-wide text-[#2d6b89]">
                            {screenshot.section}
                          </p>
                          <div className="mt-1">
                            {screenshot.rows.map(([label, value]) => (
                              <div key={label} className="grid grid-cols-[0.8fr_1.2fr] border-b border-gray-100 py-1 text-[6.5px] leading-tight">
                                <span className="text-gray-500">{label}</span>
                                <span className="text-right font-semibold text-gray-800">{value}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                    <div className="border-t border-gray-100 px-3 py-2.5">
                      <p className="text-xs font-semibold text-brand-blue">{screenshot.title}</p>
                      <p className="mt-0.5 text-[10px] text-brand-white-600">Captured today at 6:12 AM</p>
                    </div>
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      </Container>
    </section>
  );
};
